"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

type SignupResponse = {
  success?: boolean;
  redirectTo?: string;
  exists?: boolean;
  warning?: string;
  error?: string;
};

const MIN_PASSWORD_LENGTH = 8;

type FieldErrors = Partial<Record<"name" | "email" | "password" | "confirmPassword" | "consent", string>>;

export default function SignupClient() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [exists, setExists] = useState(false);

  function clearFieldError(field: keyof FieldErrors) {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setGeneralError(null);
    setExists(false);

    // Issue 42.2 — validate every required field in a single pass and
    // surface every error inline. The previous flow only checked
    // password length and let the browser's native email validation
    // catch the rest one at a time.
    const errors: FieldErrors = {};
    if (!name.trim()) errors.name = "Enter your name.";
    if (!email.trim()) errors.email = "Enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = "Enter a valid email address.";
    if (!password) errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    else if (password.length < MIN_PASSWORD_LENGTH) errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (!confirmPassword) errors.confirmPassword = "Re-enter your password.";
    else if (confirmPassword !== password) errors.confirmPassword = "Passwords don't match.";
    if (!consent) errors.consent = "Accept the Terms and Privacy Policy to continue.";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, name: name.trim() }),
      });
      const data = (await res.json().catch(() => ({}))) as SignupResponse;

      if (data.exists) {
        setExists(true);
        return;
      }
      if (!res.ok || data.error) {
        setGeneralError(data.error ?? "Something went wrong. Please try again.");
        return;
      }

      router.push(data.redirectTo ?? "/onboarding/start");
      router.refresh();
    } catch {
      setGeneralError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const passwordType = showPassword ? "text" : "password";

  return (
    <div className="auth-column">
      <h1 className="auth-headline">Start your 3-day free trial.</h1>

      <form onSubmit={handleSubmit} className="auth-panel" autoComplete="on" noValidate>
        {/* Issue 42.6 — Name was previously captured only by Stripe at
            checkout, leaving the Ranksector account record without one
            until the user paid. Required at sign-up so transactional
            emails can address the user by name immediately. */}
        <div className="auth-field">
          <label htmlFor="name" className="auth-label">Name</label>
          <input
            id="name"
            name="name"
            type="text"
            className="auth-input"
            placeholder="Your name"
            autoComplete="name"
            required
            aria-invalid={fieldErrors.name ? true : undefined}
            value={name}
            onChange={(e) => { setName(e.target.value); clearFieldError("name"); }}
          />
          {fieldErrors.name && <p className="auth-error" role="alert">{fieldErrors.name}</p>}
        </div>

        <div className="auth-field">
          <label htmlFor="email" className="auth-label">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            className="auth-input"
            placeholder="you@company.com"
            autoComplete="email"
            required
            spellCheck={false}
            aria-invalid={fieldErrors.email ? true : undefined}
            value={email}
            onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }}
          />
          {fieldErrors.email && <p className="auth-error" role="alert">{fieldErrors.email}</p>}
        </div>

        {/* Issue 42.4 — single show-password toggle that doubles for
            both the password and confirm-password fields so a typo in
            either is easy to spot. */}
        <div className="auth-field">
          <div className="auth-field-row">
            <label htmlFor="password" className="auth-label" style={{ marginBottom: 0 }}>Password</label>
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="auth-link"
              aria-pressed={showPassword}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <input
            id="password"
            name="password"
            type={passwordType}
            className="auth-input"
            placeholder="At least 8 characters"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
            aria-invalid={fieldErrors.password ? true : undefined}
            value={password}
            onChange={(e) => { setPassword(e.target.value); clearFieldError("password"); }}
          />
          {fieldErrors.password && <p className="auth-error" role="alert">{fieldErrors.password}</p>}
        </div>

        {/* Issue 42.3 — confirm-password field catches typos at sign-up.
            A full strength meter (zxcvbn) was considered but adds ~30KB
            gzipped to the sign-up bundle; the confirm field plus the
            8-char minimum is the minimum reasonable gate without it. */}
        <div className="auth-field">
          <label htmlFor="confirmPassword" className="auth-label">Confirm password</label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={passwordType}
            className="auth-input"
            placeholder="Re-enter your password"
            autoComplete="new-password"
            required
            aria-invalid={fieldErrors.confirmPassword ? true : undefined}
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError("confirmPassword"); }}
          />
          {fieldErrors.confirmPassword && <p className="auth-error" role="alert">{fieldErrors.confirmPassword}</p>}
        </div>

        {/* Issue 42.1 — required ToS/Privacy consent checkbox, unchecked
            by default. The server side captures the timestamp on the
            user record (handled in /api/signup). */}
        <div className="auth-field" style={{ marginBottom: 16 }}>
          <label className="auth-checkbox-row">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => { setConsent(e.target.checked); clearFieldError("consent"); }}
              aria-invalid={fieldErrors.consent ? true : undefined}
              required
            />
            <span>
              I agree to the{" "}
              <Link href="/company/terms-and-conditions" target="_blank" rel="noopener">Terms of Service</Link>
              {" "}and{" "}
              <Link href="/company/privacy-policy" target="_blank" rel="noopener">Privacy Policy</Link>.
            </span>
          </label>
          {fieldErrors.consent && <p className="auth-error" role="alert">{fieldErrors.consent}</p>}
        </div>

        {generalError && (
          <div className="auth-alert" role="alert">
            {generalError}
          </div>
        )}

        {exists && (
          <div className="auth-alert-notice">
            This email already has an account.{" "}
            <Link href="/login" className="auth-link" style={{ fontWeight: 600 }}>
              Log in instead
            </Link>
            .
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="auth-submit"
        >
          {submitting ? "Creating account…" : "Start 3-day free trial"}{" "}
          <span aria-hidden="true">→</span>
        </button>

        <div className="auth-switch">
          Already have an account? <Link href="/login">Log in →</Link>
        </div>
      </form>
    </div>
  );
}
